import { jwtDecode } from 'jwt-decode';
import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Permission, Session, SessionResponse, SignInRequest } from '@models/auth.model';
import { HttpService } from '@services/http.service';
import { CookieOptions, CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { Nullable } from 'primeng/ts-helpers';
import { Store } from '@ngrx/store';
import { selectPermissions } from '@app/store/selectors/common.selector';
import { BaseResponseModel } from '@models/app.model';
import { loadPermission, loadSession } from '@app/store/actions/common.action';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private session$ = new BehaviorSubject<Session | null>(null);
    private store = inject(Store);
    private permissions$ = this.store.select(selectPermissions);
    
    constructor(
        private _http: HttpService,
        private _cookieService: CookieService
    ) {}

    /**
     * Get session
     * @readonly
     * @type {(Observable<Session | null>)}
     * @memberof AuthService
     */
    public get session(): Observable<Session | null> {
        return this.session$.asObservable();
    }

    private _getExpJwt(token: string): number | undefined {
        try {
            const jwt = jwtDecode(token);
            return jwt.exp;
        } catch (error) {
            return undefined;
        }
    }
    /**
     * Update session
     *
     * @param {Session} session
     * @memberof AuthService
     */
    public updateSession(session: Session | null, sessionKey?: string): void {
        this.session$.next(session);
        if (sessionKey) {
            const jwt = this._getExpJwt(sessionKey);
            const cookieOptions: CookieOptions = {
                path: '/',
                expires: jwt ? new Date(jwt * 1000) : undefined,
                secure: true,
            };
            this._cookieService.set(environment.cookieName, sessionKey, cookieOptions);
            return;
        }

        if (!session && !sessionKey) {
            this._cookieService.delete(environment.cookieName, '/');
        }
    }

    public loadPermissionByUserId(userId: number): Observable<BaseResponseModel<Permission[]>> {
        return this._http.get('Auth/GetPermission', {
            params: new HttpParams({ fromObject: { id: userId } }),
        });
    }

    /**
     * Get session from server
     *
     * @return {*}  {Observable<Session>}
     * @memberof AuthService
     */
    public getSessionAsync(): Observable<Session> {
        return this._http.get('Auth/GetSession');
    }

    /**
     * Check Auth
     *
     * @return {*}  {Observable<boolean>}
     * @memberof AuthService
     */

    public isAuthenticated(): Observable<boolean> {
        return this.getSessionAsync().pipe(
            switchMap((session) => {
                if (session) {
                    return forkJoin([of(session), this.loadPermissionByUserId(session.UserId)]);
                }

                return of([]);
            }),
            map(([session, permissions]) => {
                if (session && permissions.Data.length) {
                    this.updateSession(session);
                    this.store.dispatch(loadSession({ session }));
                    this.store.dispatch(loadPermission({ permissions: permissions.Data }));
                    return true;
                }
                return false;
            }),
            catchError(() => of(false))
        );
    }

    /**
     * Get session by google token
     *
     * @param {string} token
     * @return {*}  {Observable<Session>}
     * @memberof AuthService
     */
    public getSessionByGoogleToken(token: string): Observable<SessionResponse> {
        return this._http.post('Auth/LoginGoogle', {
            Token: token,
        });
    }

    /**
     * Sigin in with username and password
     *
     * @param {SignInRequest} req
     * @return {*}  {Observable<SessionResponse>}
     * @memberof AuthService
     */
    public login(req: SignInRequest): Observable<SessionResponse> {
        return this._http.post('Auth/LoginSystem', req);
    }

    /**
     * Remove cookie value
     *
     * @memberof AuthService
     */
    public logout() {
        this.updateSession(null);
    }

    public findPermissionByController(
        controller: string,
        action: string | string[]
    ): Observable<Nullable<Permission>> {
        return this.permissions$.pipe(
            map((permissions) => {
                if (!permissions.length) {
                    return null;
                }
                return permissions.find((p) => {
                    if (Array.isArray(action)) {
                        return p.ControllerName === controller && action.includes(p.ActionName);
                    }

                    return p.ControllerName === controller && p.ActionName === action;
                });
            })
        );
    }
}
